import { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Select,
  SelectItem,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Chip,
  Card,
  CardBody,
  Tooltip,
  Switch,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import Header from '../../components/layouts/Header';
import { KpiCard, PageLayout } from '../../components/ui';
import { getReceiversByExporter } from 'common/src/api/exporterReceivers';
import { ExporterReceiver } from 'common/src/utils/types';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { getCurrencies, Currency } from 'common/src/api/currencies';

// Types
interface PaymentPlan {
  id: string;
  receiver: string;
  amount: number;
  currency: string;
  date: string;
  season: string;
  species: string;
}

interface Payment {
  id: string;
  receiver: string;
  localCurrencyAmount: number;
  localCurrency: string;
  usdAmount: number;
  depositCurrency: string;
  date: string;
  season: string;
  species: string;
  concept: string;
}

// Mock data for settlements (liquidaciones)
const mockSettlements = {
  'KINGO': 7000,
  'RIVERKING': 3000,
};


export default function CuentaCorrientePage() {
  // Store hooks
  const { userProfile, darkMode } = useUserStore();

  // State for currencies and receivers
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [receivers, setReceivers] = useState<ExporterReceiver[]>([]);

  // State for payment plans
  const [paymentPlans, setPaymentPlans] = useState<PaymentPlan[]>([
    {
      id: '1',
      receiver: 'KINGO',
      amount: 10000,
      currency: 'USD',
      date: '2025-12-02',
      season: '2025',
      species: 'Cherry',
    },
    {
      id: '3',
      receiver: 'KINGO',
      amount: 5000,
      currency: 'USD',
      date: '2025-08-15', // Future date
      season: '2025',
      species: 'Cherry',
    },
    {
      id: '2',
      receiver: 'RIVERKING',
      amount: 3500,
      currency: 'USD',
      date: '2025-02-02',
      season: '2025',
      species: 'Cherry',
    },

  ]);

  // State for payments
  const [payments, setPayments] = useState<Payment[]>([
    {
      id: '1',
      receiver: 'KINGO',
      localCurrencyAmount: 20000,
      localCurrency: 'RMB',
      usdAmount: 2500,
      depositCurrency: 'USD',
      date: '2025-02-02',
      season: '2025',
      species: 'Cherry',
      concept: 'ANTICIPO',
    },
    {
      id: '2',
      receiver: 'RIVERKING',
      localCurrencyAmount: 10000,
      localCurrency: 'RMB',
      usdAmount: 5000,
      depositCurrency: 'RMB',
      date: '2025-02-02',
      season: '2025',
      species: 'Cherry',
      concept: 'LIQUIDACION',
    },
  ]);


  // State for forms
  const [newPaymentPlan, setNewPaymentPlan] = useState<Partial<PaymentPlan>>({});
  const [newPayment, setNewPayment] = useState<Partial<Payment>>({});

  // Dialog states
  const { isOpen: isPlanAnticipoOpen, onOpen: onPlanAnticipoOpen, onOpenChange: onPlanAnticipoOpenChange } = useDisclosure();
  const { isOpen: isPagoOpen, onOpen: onPagoOpen, onOpenChange: onPagoOpenChange } = useDisclosure();
  const { isOpen: isDetalleClienteOpen, onOpen: onDetalleClienteOpen, onOpenChange: onDetalleClienteOpenChange } = useDisclosure();

  // Selected client for actions
  const [selectedClientForAction, setSelectedClientForAction] = useState<string>('');
  const [selectedReceiver, setSelectedReceiver] = useState<string>('');
  const [considerCurrentDate, setConsiderCurrentDate] = useState<boolean>(false);

  // Fetch receivers when component mounts
  useEffect(() => {
    const fetchReceivers = async () => {
      if (!userProfile?.exporter_id) {
        return;
      }

      try {
        const receiversData = await getReceiversByExporter(userProfile.exporter_id);
        setReceivers(receiversData);
      } catch (error) {
        console.error('Error fetching receivers:', error);
      }
    };

    fetchReceivers();
  }, [userProfile?.exporter_id]);

  useEffect(() => {
    getCurrencies().then((data) => setCurrencies(data || [])).catch(() => {});
  }, []);

  // Get unique receivers from API data, payment plans, and payments
  const allReceivers = Array.from(
    new Set([
      ...receivers.map(r => r.name),
      ...paymentPlans.map(p => p.receiver),
      ...payments.map(p => p.receiver),
    ])
  );


  // Calculate dashboard metrics
  const calculateDashboardMetrics = () => {
    const currentDate = new Date();
    const receiverData: Record<string, {
      // Old fields for backward compatibility
      porAnticipos: number;
      porLiquidaciones: number;
      totalPorCobrar: number;
      cumplimientoAnticipos: number;
      cumplimientoLiquidaciones: number;
      cumplimientoTotal: number;
      totalPlan: number;
      totalPayments: number;
      totalSettlements: number;
      // New fields for updated table
      anticiposPlanificados: number;
      anticiposPagados: number;
      anticiposPorCobrar: number;
      liquidadoSobreAnticipos: number;
      liquidadoSobreAnticiposPagado: number;
      liquidadoSobreAnticiposPorCobrar: number;
      totalPorCobrarNew: number;
      cumplimientoPorcentaje: number;
    }> = {};

    allReceivers.forEach(receiver => {
      const receiverPlans = paymentPlans.filter(p => p.receiver === receiver);
      const receiverPayments = payments.filter(p => p.receiver === receiver);
      const totalSettlements = mockSettlements[receiver as keyof typeof mockSettlements] || 0;

      // Calculate totalPlan considering dates if toggle is enabled
      let totalPlan: number;
      if (considerCurrentDate) {
        // Only include plans where the date has already passed or is today
        totalPlan = receiverPlans
          .filter(plan => new Date(plan.date) <= currentDate)
          .reduce((sum, plan) => sum + plan.amount, 0);
      } else {
        // Include all plans regardless of date
        totalPlan = receiverPlans.reduce((sum, plan) => sum + plan.amount, 0);
      }

      const totalPayments = receiverPayments.reduce((sum, payment) => sum + payment.usdAmount, 0);

      // Por Anticipos = Plan - Pagos que cubran el plan
      const porAnticipos = Math.max(0, totalPlan - totalPayments);

      // Por Liquidaciones = Total Liquidado - Plan - Pagos
      // Settlements are always considered as they represent completed sales
      const porLiquidaciones = Math.max(0, totalSettlements - totalPlan - totalPayments);

      const cumplimientoTotal = Math.min(100, (((totalSettlements - totalPlan) - porLiquidaciones) / (totalSettlements - totalPlan)) * 100);
      const cumplimientoAnticipos = Math.min(100, (((totalPlan - porAnticipos) / totalPlan) * 100));
      const cumplimientoLiquidaciones = Math.min(100, (((totalSettlements - totalPlan) - porLiquidaciones) / (totalSettlements - totalPlan)) * 100);

      // Calculate new fields for updated table structure
      const anticiposPlanificados = totalPlan;
      const anticiposPagados = Math.min(totalPayments, totalPlan); // Payments applied to anticipos first
      const anticiposPorCobrar = Math.max(0, totalPlan - anticiposPagados);

      // Liquidado sobre anticipos = total settlements - planned anticipos
      const liquidadoSobreAnticipos = Math.max(0, totalSettlements - totalPlan);

      // Payments beyond anticipos go to liquidations
      const paymentsForLiquidations = Math.max(0, totalPayments - totalPlan);
      const liquidadoSobreAnticiposPagado = Math.min(paymentsForLiquidations, liquidadoSobreAnticipos);
      const liquidadoSobreAnticiposPorCobrar = Math.max(0, liquidadoSobreAnticipos - liquidadoSobreAnticiposPagado);

      const totalPorCobrarNew = anticiposPorCobrar + liquidadoSobreAnticiposPorCobrar;

      // Calculate cumplimiento percentage for the "Pagados" column
      const totalExpected = totalSettlements;
      const totalPaid = totalPayments;
      const cumplimientoPorcentaje = totalExpected > 0 ? Math.min(100, (totalPaid / totalExpected) * 100) : 0;

      receiverData[receiver] = {
        // Old fields for backward compatibility
        porAnticipos,
        porLiquidaciones,
        totalPorCobrar: porAnticipos + porLiquidaciones,
        cumplimientoAnticipos: cumplimientoAnticipos,
        cumplimientoLiquidaciones: cumplimientoLiquidaciones,
        cumplimientoTotal: cumplimientoTotal,
        totalPlan,
        totalPayments,
        totalSettlements,
        // New fields for updated table
        anticiposPlanificados,
        anticiposPagados,
        anticiposPorCobrar,
        liquidadoSobreAnticipos,
        liquidadoSobreAnticiposPagado,
        liquidadoSobreAnticiposPorCobrar,
        totalPorCobrarNew,
        cumplimientoPorcentaje,
      };
    });

    return receiverData;
  };

  const dashboardMetrics = calculateDashboardMetrics();

  // Prepare chart data
  const prepareChartData = () => {
    const filteredData = Object.entries(dashboardMetrics)
      .filter(([receiver]) => !selectedReceiver || receiver === selectedReceiver)
      .sort(([, a], [, b]) => b.totalPorCobrarNew - a.totalPorCobrarNew); // Sort by Por Cobrar desc

    const categories: string[] = [];
    const anticiposData: (number | null)[] = [];
    const liquidacionesGreyData: (number | null)[] = [];
    const liquidacionesBlueData: (number | null)[] = [];
    const pagosData: (number | null)[] = [];
    const clientGroups: string[] = [];

    filteredData.forEach(([receiver, metrics], clientIndex) => {
      // Add spacing between clients (except for the first one)
      if (clientIndex > 0) {
        categories.push(''); // Empty separator
        anticiposData.push(null);
        liquidacionesGreyData.push(null);
        liquidacionesBlueData.push(null);
        pagosData.push(null);
      }

      // Add client header
      categories.push(`${receiver}`);
      anticiposData.push(null);
      liquidacionesGreyData.push(null);
      liquidacionesBlueData.push(null);
      pagosData.push(null);

      // Add categories for each client (3 rows per client)
      categories.push('  Anticipos');
      categories.push('  Liquidaciones');
      categories.push('  Pagos');

      // Anticipos row: only shows plan anticipos in blue
      anticiposData.push(metrics.totalPlan > 0 ? metrics.totalPlan : null);
      anticiposData.push(null);
      anticiposData.push(null);

      // Liquidaciones row: grey until anticipos level, then blue for excess
      liquidacionesGreyData.push(null);
      const greyAmount = Math.min(metrics.totalSettlements, metrics.totalPlan);
      liquidacionesGreyData.push(greyAmount > 0 ? greyAmount : null);
      liquidacionesGreyData.push(null);

      // Liquidaciones excess (blue)
      liquidacionesBlueData.push(null);
      const blueAmount = Math.max(0, metrics.totalSettlements - metrics.totalPlan);
      liquidacionesBlueData.push(blueAmount > 0 ? blueAmount : null);
      liquidacionesBlueData.push(null);

      // Pagos row: only shows payments in green
      pagosData.push(null);
      pagosData.push(null);
      pagosData.push(metrics.totalPayments > 0 ? metrics.totalPayments : null);

      clientGroups.push(receiver);
    });

    return {
      categories,
      clientGroups,
      series: [
        {
          name: 'Plan Anticipos',
          data: anticiposData,
          color: '#6c3fcf' // Blue
        },
        {
          name: 'Liquidaciones (Anticipos)',
          data: liquidacionesGreyData,
          color: '#6B7280' // Grey
        },
        {
          name: 'Liquidaciones (Exceso)',
          data: liquidacionesBlueData,
          color: '#1E40AF' // Darker Blue
        },
        {
          name: 'Pagos',
          data: pagosData,
          color: '#10B981' // Green
        }
      ]
    };
  };

  const chartData = prepareChartData();

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: Math.max(300, chartData.categories.length * 25),
      foreColor: darkMode ? '#A1A1AA' : '#71717A',
      stacked: true,
      toolbar: {
        show: false
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        dataLabels: {
          total: {
            enabled: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter: function (val: any) {
              return val > 0 ? `$${Number(val).toLocaleString()}` : '';
            }
          }
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    dataLabels: {
      enabled: false
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        formatter: function (val) {
          return `$${Number(val).toLocaleString()}`;
        }
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    yaxis: {
      labels: {
        style: {
          colors: '#6B7280',
          fontSize: '11px',
          fontWeight: 'normal'
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    fill: {
      opacity: 1
    },
    // legend: {
    //   position: 'top',
    //   horizontalAlign: 'left',
    //   offsetX: 40
    // },
    colors: ['#6c3fcf', '#6B7280', '#1E40AF', '#10B981'],
    tooltip: {
      y: {
        formatter: function (val) {
          return val !== null && val > 0 ? `$${val.toLocaleString()}` : '';
        }
      }
    }
  };


  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <PageLayout className='overflow-y-auto'>
        <Header title="Cuenta Corriente" description="Gestión de anticipos y pagos" />


        {/* Receiver Filter and Date Toggle */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 sm:items-center justify-start">
          <Select
            label="Filtrar por Cliente"
            placeholder="Seleccionar cliente"
            selectedKeys={selectedReceiver ? [selectedReceiver] : []}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0] as string;
              setSelectedReceiver(key || '');
            }}
            className="max-w-xs"
          >
            {[
              <SelectItem key="" value="">Todos los clientes</SelectItem>,
              ...allReceivers.map((receiver) => (
                <SelectItem key={receiver} value={receiver}>
                  {receiver}
                </SelectItem>
              ))
            ]}
          </Select>

          <div className="flex flex-col">
            <Switch
              isSelected={considerCurrentDate}
              onValueChange={setConsiderCurrentDate}
              size="sm"
              color="primary"
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {considerCurrentDate ? "Por Cobrar Ahora" : "Por Cobrar Temporada"}
                </span>
                <span className="text-xs text-default-500">
                  {considerCurrentDate
                    ? "Solo anticipos con fecha vencida"
                    : "Todos los anticipos programados"
                  }
                </span>
              </div>
            </Switch>
          </div>
        </div>

        {/* Client Summary Cards */}
        <div className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {Object.entries(dashboardMetrics)
              .filter(([receiver]) => !selectedReceiver || receiver === selectedReceiver)
              .sort(([, a], [, b]) => b.totalPorCobrarNew - a.totalPorCobrarNew) // Sort by total desc
              .map(([receiver, metrics]) => (
                <Tooltip
                  key={receiver}
                  content={
                    <div className="p-3 space-y-2">
                      <div className="font-semibold text-sm">{receiver} - Detalle</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>Por Anticipos:</span>
                          <span className="font-mono">${metrics.porAnticipos.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Por Liquidaciones:</span>
                          <span className="font-mono">${metrics.porLiquidaciones.toLocaleString()}</span>
                        </div>
                        <div className="border-t pt-1 mt-1">
                          <div className="flex justify-between font-semibold">
                            <span>Total:</span>
                            <span className="font-mono">${metrics.totalPorCobrarNew.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                  placement="top"
                  delay={300}
                >
                  <div className="min-w-0" tabIndex={0}>
                    <KpiCard
                      label={receiver}
                      value={`$${metrics.totalPorCobrarNew.toLocaleString()}`}
                      detail="Por Cobrar"
                    />
                  </div>
                </Tooltip>
              ))}
          </div>
        </div>

        {/* <p className="text-lg font-semibold mb-4">Resumen</p>  */}


        {/* Cards Summary */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {Object.entries(dashboardMetrics)
                .filter(([receiver]) => !selectedReceiver || receiver === selectedReceiver)
                .map(([receiver, metrics]) => (
                <Card key={receiver} shadow='sm'>
                  <CardBody>
                    <h4 className="font-semibold text-lg mb-3">{receiver}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-default-600">Por Anticipos:</span>
                        <Chip color="warning" size="sm">
                          ${metrics.porAnticipos.toLocaleString()}
                        </Chip>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-default-600">Por Liquidaciones:</span>
                        <Chip color="secondary" size="sm">
                          ${metrics.porLiquidaciones.toLocaleString()}
                        </Chip>
                      </div>
                      <Divider />
                      <div className="flex justify-between font-semibold">
                        <span>Total Por Cobrar:</span>
                        <Chip size="sm">
                          ${metrics.totalPorCobrarNew.toLocaleString()}
                        </Chip>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div> */}

        <Tabs aria-label="Visualización Tabs" className="w-full">
          <Tab key="table" title={
            <div className="flex items-center space-x-2">
              <span>Tabla</span>
            </div>
          }>


            <Table aria-label="Dashboard Detailed Table">
              <TableHeader>
                <TableColumn>CLIENTE</TableColumn>
                <TableColumn>ANTICIPOS PLANIFICADOS {considerCurrentDate ? "VENCIDOS" : "TEMPORADA"}</TableColumn>
                <TableColumn>+ LIQUIDADO SOBRE ANTICIPOS</TableColumn>
                <TableColumn>= TOTAL</TableColumn>
                <TableColumn>- PAGOS</TableColumn>
                <TableColumn>= POR COBRAR {considerCurrentDate ? "AHORA" : "TEMPORADA"}</TableColumn>
                <TableColumn>ACCIONES</TableColumn>
              </TableHeader>
              <TableBody>
                {Object.entries(dashboardMetrics)
                  .filter(([receiver]) => !selectedReceiver || receiver === selectedReceiver)
                  .sort(([, a], [, b]) => b.totalPorCobrarNew - a.totalPorCobrarNew) // Sort by Por Cobrar desc
                  .map(([receiver, metrics]) => {
                    return (
                      <TableRow key={receiver}>
                        <TableCell className="font-semibold">{receiver}</TableCell>

                        {/* Anticipos Planificados */}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              aria-label="Agregar Anticipo"
                              onPress={() => {
                                setSelectedClientForAction(receiver);
                                onPlanAnticipoOpen();
                              }}
                            >
                              <Icon icon='tabler:plus' />
                            </Button>
                            <span>${metrics.anticiposPlanificados.toLocaleString()}</span>
                          </div>
                        </TableCell>



                        {/* Liquidado Sobre Anticipos */}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>${metrics.liquidadoSobreAnticipos.toLocaleString()}</span>

                          </div>
                        </TableCell>

                        {/* Total */}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>${(metrics.liquidadoSobreAnticipos + metrics.anticiposPlanificados).toLocaleString()}</span>

                          </div>
                        </TableCell>



                        {/* PAGOS */}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              aria-label="Agregar Pago"
                              onPress={() => {
                                setSelectedClientForAction(receiver);
                                onPagoOpen();
                              }}
                            >
                              <Icon icon='tabler:plus' />
                            </Button>
                            <span>${metrics.totalPayments.toLocaleString()}</span>

                          </div>
                        </TableCell>

                        {/* POR COBRAR */}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>${metrics.totalPorCobrarNew.toLocaleString()}</span>

                          </div>
                        </TableCell>

                        {/* Acciones */}
                        <TableCell>
                          <div className="flex items-center">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={() => {
                                setSelectedClientForAction(receiver);
                                onDetalleClienteOpen();
                              }}
                              aria-label="Ver detalle del cliente"
                            >
                              <Icon icon='tabler:eye' />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Tab>



          <Tab key="chart" title={
            <div className="flex items-center space-x-2">
              <span>Gráfico</span>
            </div>
          }>
            <Card className="my-4">

              <CardBody>
                <div className="w-full">
                  <ReactApexChart
                    options={chartOptions}
                    series={chartData.series}
                    type="bar"
                    height={chartOptions.chart?.height}
                  />
                </div>


              </CardBody>
            </Card>
          </Tab>
        </Tabs>


      </PageLayout>

      {/* Plan Anticipo Dialog */}
      <Modal
        isOpen={isPlanAnticipoOpen}
        onOpenChange={onPlanAnticipoOpenChange}
        size="2xl"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Agregar Plan de Anticipo - {selectedClientForAction}
              </ModalHeader>
              <ModalBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Cliente"
                    value={selectedClientForAction}
                    isReadOnly
                    variant="faded"
                  />
                  <Input
                    type="number"
                    label="Monto USD"
                    placeholder="0.00"
                    value={newPaymentPlan.amount?.toString() || ''}
                    onChange={(e) => setNewPaymentPlan({
                      ...newPaymentPlan,
                      amount: parseFloat(e.target.value) || 0
                    })}
                  />

                  <Input
                    type="date"
                    label="Fecha Proyectada de Pago"
                    value={newPaymentPlan.date || ''}
                    onChange={(e) => setNewPaymentPlan({
                      ...newPaymentPlan,
                      date: e.target.value
                    })}
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  Cancelar
                </Button>
                <Button
                  color="primary"
                  onPress={() => {
                    const newPlan: PaymentPlan = {
                      id: (paymentPlans.length + 1).toString(),
                      receiver: selectedClientForAction,
                      amount: newPaymentPlan.amount || 0,
                      currency: newPaymentPlan.currency || 'USD',
                      date: newPaymentPlan.date || new Date().toISOString().split('T')[0],
                      season: '2025',
                      species: 'Cherry',
                    };
                    setPaymentPlans([...paymentPlans, newPlan]);
                    setNewPaymentPlan({});
                    onClose();
                  }}
                >
                  Agregar
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Pago Dialog */}
      <Modal
        isOpen={isPagoOpen}
        onOpenChange={onPagoOpenChange}
        size="2xl"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Agregar Pago - {selectedClientForAction}
              </ModalHeader>
              <ModalBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Cliente"
                    value={selectedClientForAction}
                    isReadOnly
                    variant="faded"
                  />
                  <Input
                    type="number"
                    label="Monto USD"
                    placeholder="0.00"
                    value={newPayment.usdAmount?.toString() || ''}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      usdAmount: parseFloat(e.target.value) || 0
                    })}
                  />
                  <Input
                    type="number"
                    label="Monto Equivalente Local (RMB, EUR, etc)"
                    placeholder="0.00"
                    value={newPayment.localCurrencyAmount?.toString() || ''}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      localCurrencyAmount: parseFloat(e.target.value) || 0
                    })}
                  />
                  <Select
                    label="Moneda Local"
                    placeholder="Selecciona moneda"
                    selectedKeys={newPayment.localCurrency ? [newPayment.localCurrency] : []}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      localCurrency: e.target.value
                    })}
                  >
                    {currencies.map((currency) => (
                      <SelectItem key={currency.value} value={currency.value}>{currency.name}</SelectItem>
                    ))}
                  </Select>


                  <div className="flex w-full col-span-2">
                    <span>Tipo de Cambio Calculado: {newPayment.localCurrencyAmount > 0 && newPayment.usdAmount > 0 && newPayment.localCurrency ? `${(newPayment.localCurrencyAmount / newPayment.usdAmount).toFixed(4)} ${newPayment.localCurrency} / USD` : 'Completa los campos para calcular el tipo de cambio'}</span>
                  </div>

                  {/* <Select
                    label="Moneda Depósito"
                    placeholder="Selecciona moneda"
                    selectedKeys={newPayment.depositCurrency ? [newPayment.depositCurrency] : []}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      depositCurrency: e.target.value
                    })}
                  >
                    <SelectItem key="USD" value="USD">USD</SelectItem>
                    <SelectItem key="EUR" value="EUR">EUR</SelectItem>
                    <SelectItem key="RMB" value="RMB">RMB</SelectItem>
                  </Select> */}
                  <Input
                    type="date"
                    label="Fecha"
                    value={newPayment.date || ''}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      date: e.target.value
                    })}
                  />
                  <Select
                    label="Concepto"
                    placeholder="Selecciona concepto"
                    selectedKeys={newPayment.concept ? [newPayment.concept] : []}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      concept: e.target.value as 'ANTICIPO' | 'LIQUIDACION'
                    })}
                  >
                    <SelectItem key="ANTICIPO" value="ANTICIPO">ANTICIPO</SelectItem>
                    <SelectItem key="LIQUIDACION" value="LIQUIDACION">LIQUIDACION</SelectItem>
                  </Select>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  Cancelar
                </Button>
                <Button
                  color="primary"
                  onPress={() => {
                    const newPaymentRecord: Payment = {
                      id: (payments.length + 1).toString(),
                      receiver: selectedClientForAction,
                      localCurrencyAmount: newPayment.localCurrencyAmount || 0,
                      localCurrency: newPayment.localCurrency || 'USD',
                      usdAmount: newPayment.usdAmount || 0,
                      depositCurrency: newPayment.depositCurrency || 'USD',
                      date: newPayment.date || new Date().toISOString().split('T')[0],
                      season: '2025',
                      species: 'Cherry',
                      concept: newPayment.concept || 'ANTICIPO',
                    };
                    setPayments([...payments, newPaymentRecord]);
                    setNewPayment({});
                    onClose();
                  }}
                >
                  Agregar
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Detalle Cliente Dialog */}
      <Modal
        isOpen={isDetalleClienteOpen}
        onOpenChange={onDetalleClienteOpenChange}
        size="5xl"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Detalle Cliente - {selectedClientForAction}
              </ModalHeader>
              <ModalBody>
                <Tabs aria-label="Cliente Detail Tabs" className="w-full">
                  <Tab key="client-plans" title="Plan de Anticipos">
                    <Table aria-label="Client Payment Plans Table">
                      <TableHeader>
                        <TableColumn>MONTO USD</TableColumn>
                        {/* <TableColumn>MONEDA</TableColumn> */}
                        <TableColumn>FECHA</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {paymentPlans
                          .filter(plan => plan.receiver === selectedClientForAction)
                          .map((plan) => (
                            <TableRow key={plan.id}>
                              <TableCell>{plan.amount.toLocaleString()}</TableCell>
                              {/* <TableCell>{plan.currency}</TableCell> */}
                              <TableCell>{plan.date}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </Tab>
                  <Tab key="client-payments" title="Pagos">
                    <Table aria-label="Client Payments Table">
                      <TableHeader>
                        <TableColumn>MONTO USD</TableColumn>
                        <TableColumn>MONTO LOCAL</TableColumn>
                        <TableColumn>MONEDA LOCAL</TableColumn>
                        <TableColumn>T/C</TableColumn>
                        {/* <TableColumn>MONEDA DEPÓSITO</TableColumn> */}
                        <TableColumn>FECHA</TableColumn>
                        <TableColumn>CONCEPTO</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {payments
                          .filter(payment => payment.receiver === selectedClientForAction)
                          .map((payment) => (
                            <TableRow key={payment.id}>
                              <TableCell>${payment.usdAmount.toLocaleString()}</TableCell>
                              <TableCell>{payment.localCurrencyAmount.toLocaleString()}</TableCell>
                              <TableCell>{payment.localCurrency}</TableCell>
                              <TableCell>{payment.localCurrencyAmount > 0 && payment.usdAmount > 0 && payment.localCurrency ? `${(payment.localCurrencyAmount / payment.usdAmount).toFixed(2)} ${payment.localCurrency} / USD` : 'Completa los campos para calcular el tipo de cambio'}</TableCell>
                              {/* <TableCell>{payment.depositCurrency}</TableCell> */}
                              <TableCell>{payment.date}</TableCell>
                              <TableCell>
                                <Chip
                                  color={payment.concept === 'ANTICIPO' ? 'warning' : payment.concept === 'LIQUIDACION' ? 'success' : 'primary'}
                                  size="sm"
                                >
                                  {payment.concept}
                                </Chip>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </Tab>
                </Tabs>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  Cerrar
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
